import React from 'react';
import { Box, IconButton, Text } from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiX, FiMenu } from 'react-icons/fi';
import { LAUNCHER } from './launcherVisualTokens';
import { getToolIconTheme } from './toolIconTheme';

const FavoriteTile = ({ app, onNavigate, onRemove }) => {
  const { Icon, label, id } = app;
  const iconTheme = getToolIconTheme(id);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      role="group"
      position="relative"
      flex={`0 0 ${LAUNCHER.appTileWidth}`}
      w={LAUNCHER.appTileWidth}
      minW={LAUNCHER.appTileWidth}
      maxW={LAUNCHER.appTileWidth}
      sx={{
        touchAction: 'none',
        '&:hover .fav-launcher-controls': { opacity: 1 },
      }}
    >
      <Box
        className="fav-launcher-controls"
        position="absolute"
        top="-4px"
        left="-4px"
        zIndex={2}
        opacity={0}
        transition="opacity 0.18s ease"
        sx={{ '&:focus-within': { opacity: 1 } }}
      >
        <IconButton
          aria-label="Reorder"
          icon={<FiMenu size={12} strokeWidth={2.25} />}
          size="xs"
          variant="ghost"
          minW="22px"
          h="22px"
          color="rgba(255, 255, 255, 0.8)"
          borderRadius="md"
          bg="rgba(32, 32, 32, 0.92)"
          backdropFilter="blur(10px)"
          boxShadow="0 2px 8px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.08)"
          _hover={{ bg: 'rgba(48, 48, 48, 0.96)', color: 'white' }}
          _focusVisible={{ opacity: 1, boxShadow: '0 0 0 2px rgba(45, 212, 191, 0.55)' }}
          {...attributes}
          {...listeners}
        />
      </Box>

      <Box
        className="fav-launcher-controls"
        position="absolute"
        top="-4px"
        right="-4px"
        zIndex={2}
        opacity={0}
        transition="opacity 0.18s ease"
        sx={{ '&:focus-within': { opacity: 1 } }}
      >
        <IconButton
          aria-label={`Remove ${label} from Favorites`}
          icon={<FiX size={12} strokeWidth={2.5} />}
          size="xs"
          variant="ghost"
          minW="22px"
          h="22px"
          color="rgba(255, 255, 255, 0.65)"
          borderRadius="md"
          bg="rgba(32, 32, 32, 0.92)"
          backdropFilter="blur(10px)"
          boxShadow="0 2px 8px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.08)"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(id);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          _hover={{ bg: 'rgba(127, 29, 29, 0.55)', color: '#fecaca' }}
          _focusVisible={{ opacity: 1, boxShadow: '0 0 0 2px rgba(248, 113, 113, 0.55)' }}
        />
      </Box>

      <Box
        as="button"
        type="button"
        onClick={() => onNavigate(app)}
        w="100%"
        minH={LAUNCHER.appTileMinH}
        py={1.5}
        px={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={1.5}
        borderRadius="lg"
        borderWidth={0}
        bg="transparent"
        cursor="pointer"
        transition="transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease"
        boxShadow={isDragging ? '0 12px 32px rgba(0, 0, 0, 0.45)' : 'none'}
        opacity={isDragging ? 0.95 : 1}
        _hover={{
          bg: 'rgba(255, 255, 255, 0.06)',
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.35)',
          '& .fav-icon-well': {
            background: iconTheme.containerHover,
            color: iconTheme.iconColor,
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.06)',
          },
        }}
        _active={{ transform: 'translateY(0)', boxShadow: 'none' }}
        _focusVisible={{
          outline: '2px solid',
          outlineColor: 'rgba(45, 212, 191, 0.75)',
          outlineOffset: '2px',
        }}
        sx={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <Box
          className="fav-icon-well"
          w={LAUNCHER.launcherIconWell}
          h={LAUNCHER.launcherIconWell}
          borderRadius="14px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color={iconTheme.iconColor}
          transition="background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease"
          sx={{
            background: iconTheme.containerBg,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
          }}
        >
          <Icon size={LAUNCHER.launcherIconSize} aria-hidden />
        </Box>
        <Text
          {...LAUNCHER.tileAppLabel}
          noOfLines={2}
          w="100%"
          maxW={LAUNCHER.appTileWidth}
          mx="auto"
          px={0.5}
          overflowWrap="break-word"
          wordBreak="break-word"
        >
          {label}
        </Text>
      </Box>
    </Box>
  );
};

export default FavoriteTile;
